// Per-day ROOM-TRANSFER overlay (operatingRoomId -> toDoctorId), local to the Board. Moving a room in a
// doctor's person card writes here; the Board then shows that room's supervising anesthesiologist as the
// target doctor instead of the allocated one, and BOTH doctors' derived in-times update (in-time = earliest
// room). Reversible — clearing removes the entry. Stored per day (survives refresh), like the site locks.
export const roomTransfersKey = (date: string) => `oc-room-transfers-${date}`;

export function loadRoomTransfers(date: string): Record<string, string> {
  try {
    const raw = localStorage.getItem(roomTransfersKey(date));
    // Accept only a plain object map; reject a JSON array (typeof [] === 'object') or any other shape so a
    // corrupt entry falls back to an empty overlay instead of poisoning room-doctor lookups.
    if (raw) { const v = JSON.parse(raw); if (v && typeof v === 'object' && !Array.isArray(v)) return v as Record<string, string>; }
  } catch { /* ignore */ }
  return {};
}

// Apply the overlay to one room's doctor list: a MOVED room reports EXACTLY [toDoctorId] (the target
// replaces whoever was allocated); an un-moved room reports its allocated doctors unchanged. This is the
// single semantics every board read site must go through so nameplate, supervision, concurrency, bullpen,
// hover, and in-time all agree on who owns a room. Pure + exported for unit testing.
export function effectiveDoctorIdsForRoom(
  allocatedDoctorIds: string[],
  operatingRoomId: string | undefined,
  transfers: Record<string, string>,
): string[] {
  const to = operatingRoomId ? transfers[operatingRoomId] : undefined;
  return to ? [to] : allocatedDoctorIds;
}

// Move a room to a doctor (toDoctorId) or clear the move (null); returns the updated map.
export function setRoomTransfer(date: string, operatingRoomId: string, toDoctorId: string | null): Record<string, string> {
  const t = loadRoomTransfers(date);
  if (toDoctorId) t[operatingRoomId] = toDoctorId; else delete t[operatingRoomId];
  try { localStorage.setItem(roomTransfersKey(date), JSON.stringify(t)); } catch { /* ignore */ }
  return t;
}

// A grouped candidate list for the person card's "Transfer all" / "Swap all" picker: providers organized by
// the SITE(S) where they are currently assigned (someone at multiple sites appears under each), with a final
// "Not yet assigned" section for available providers who hold no rooms. Sites sorted by `siteOrder` (else by
// name), providers by name. `coinOf`/`orderOf` (optional) enrich each group with its site coin (color +
// acronym) and each candidate with their pecking-order number, so the picker can render colored coin section
// headers + peck badges instead of a bare dropdown.
export interface TransferCandidateEntry { id: string; name: string; order?: number | null; }
export interface TransferCandidateGroup {
  label: string;
  hospitalId?: string;   // the site's id — absent on the "Not yet assigned" group
  color?: string;        // site coin color (when coinOf is supplied)
  acronym?: string;      // site coin acronym (when coinOf is supplied)
  docs: TransferCandidateEntry[];
}

export function groupTransferCandidates(
  targetIds: string[],
  assignedDoctorsBySite: Map<string, Set<string>>,
  nameOf: (id: string) => string,
  hospitalNameOf: (hid: string) => string,
  // Optional site order (hospitalIds, e.g. the board's column order). When given, groups are ordered by it —
  // ids not in the list sort after all known ones, then by name — so the picker's sites match the board. When
  // omitted, sites stay sorted alphabetically by name (the original behavior).
  siteOrder?: readonly string[],
  // Optional: the site coin (color + acronym) for a hospitalId, so the picker can show a colored coin header.
  coinOf?: (hid: string) => { color: string; acronym: string } | undefined,
  // Optional: a candidate's pecking-order number (null/undefined = shift worker), shown as a small badge.
  orderOf?: (id: string) => number | null | undefined,
): TransferCandidateGroup[] {
  const targets = new Set(targetIds);
  const placed = new Set<string>();
  const groups: TransferCandidateGroup[] = [];
  const byName = (a: string, b: string) => hospitalNameOf(a).localeCompare(hospitalNameOf(b));
  let siteCompare = byName;
  if (siteOrder) {
    const rank = new Map<string, number>();
    siteOrder.forEach((hid, i) => rank.set(hid, i));
    const rankOf = (hid: string) => (rank.has(hid) ? rank.get(hid)! : Number.MAX_SAFE_INTEGER);
    siteCompare = (a: string, b: string) => (rankOf(a) - rankOf(b)) || byName(a, b);
  }
  const entryOf = (id: string): TransferCandidateEntry => {
    const o = orderOf ? orderOf(id) : undefined;
    return { id, name: nameOf(id), order: o == null ? null : o };
  };
  const siteIds = [...assignedDoctorsBySite.keys()].sort(siteCompare);
  for (const hid of siteIds) {
    const docs = [...(assignedDoctorsBySite.get(hid) || [])].filter((id) => targets.has(id)).sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
    if (!docs.length) continue;
    docs.forEach((id) => placed.add(id));
    const coin = coinOf ? coinOf(hid) : undefined;
    groups.push({ label: hospitalNameOf(hid) || 'Site', hospitalId: hid, color: coin?.color, acronym: coin?.acronym, docs: docs.map(entryOf) });
  }
  const unassigned = targetIds.filter((id) => !placed.has(id)).sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
  if (unassigned.length) groups.push({ label: 'Not yet assigned', docs: unassigned.map(entryOf) });
  return groups;
}

// Clear EVERY room transfer for a date. Used when a new simulation replaces the day, so old room-to-doctor
// moves (keyed by the stable operatingRoomId) don't linger on the fresh board.
export function clearRoomTransfers(date: string): void {
  try { localStorage.removeItem(roomTransfersKey(date)); } catch { /* ignore */ }
}
